<template>
  <div class="pn" :class="{ 'is-dragging': dragging, 'is-embedded': embedded }" @dragover.prevent="onDragOver" @dragleave="onDragLeave" @drop.prevent="onDrop">
    <PageHeader v-if="!embedded" :title="title" :subtitle="subtitle" :breadcrumbs="NOTEBOOK_CRUMBS">
      <template #title-suffix>
        <a :href="$router.resolve(NOTEBOOKS_PATH).href" class="order-first inline-flex items-center text-gray-600 hover:text-gray-900" aria-label="Back" @click.prevent="backTo($router, NOTEBOOKS_PATH)">
          <font-awesome-icon :icon="['fas', 'arrow-left']" class="w-3.5 h-3.5" />
        </a>
        <button v-if="canEdit" type="button" class="ml-2 text-gray-400 hover:text-gray-700 text-[11px]" title="Rename" @click="startRename">rename</button>
      </template>
      <template #actions>
        <a :href="downloadHref" class="btn-secondary-sm pn-action" title="Download this notebook as .ipynb" @click.prevent="downloadNotebook">.ipynb</a>
        <label v-if="canEdit" class="btn-secondary-sm pn-action cursor-pointer" :class="{ 'opacity-60': uploading }">
          {{ uploading ? uploadPhase : 'Upload file' }}
          <input type="file" class="hidden" accept=".csv,.xlsx,.xls,.xlsm,.tsv,.txt,.json,.ipynb" :disabled="uploading" @change="onPick">
        </label>
        <button v-if="canEdit" type="button" class="btn-secondary-sm pn-action" :disabled="rerunning || !runnable.length" @click="rerunAll">{{ rerunning ? (rerunPhase || 'Running…') : 'Run all' }}</button>
        <button v-if="canEdit && kernelsLive.length" type="button" class="btn-secondary-sm pn-action" title="Restart the session's Python kernel; variables are lost and the next run starts fresh" @click="restartKernel">Restart kernel</button>
        <button v-if="canEdit" type="button" class="btn-secondary-sm pn-action" :disabled="publishing" title="Freeze this notebook as a numbered version with the record of every frame it read and wrote" @click="publish">{{ publishing ? 'Publishing…' : 'Publish' }}</button>
        <router-link :to="reportPath" class="btn-secondary-sm pn-action">Report<span v-if="reportCount" class="ml-1 text-gray-500">{{ reportCount }}</span></router-link>
        <button v-if="canEdit" type="button" class="btn-primary-sm pn-action" @click="agentOpen = !agentOpen">{{ agentOpen ? 'Hide agent' : 'Agent' }}</button>
      </template>
      <template v-if="renaming" #bottom>
        <form class="px-6 pb-3 flex items-center gap-2" @submit.prevent="rename">
          <input ref="renameInput" v-model="renameDraft" type="text" class="w-96 px-3 py-1.5 border border-gray-300 rounded text-[12px]" aria-label="Project name">
          <button type="submit" class="btn-primary-sm">Save</button>
          <button type="button" class="btn-secondary-sm" @click="renaming = false">Cancel</button>
        </form>
      </template>
    </PageHeader>
    <div v-else class="pn-toolbar">
      <span class="pn-toolbar-title">{{ title }}</span>
      <router-link :to="notebookPath(notebookId)" class="pn-muted" title="Open this notebook on its own page">open</router-link>
      <span class="pn-toolbar-actions">
        <a :href="downloadHref" class="btn-secondary-sm pn-action" title="Download as .ipynb" @click.prevent="downloadNotebook">.ipynb</a>
      </span>
    </div>

    <div v-if="error" class="mx-6 mt-4 rounded-md bg-red-tint px-4 py-3 text-gray-900 text-[12px]">{{ error }}</div>
    <div v-if="notice" class="mx-6 mt-4 rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-gray-900 text-[12px]">{{ notice }} <button type="button" class="ml-2 text-gray-500" @click="notice = ''">dismiss</button></div>

    <div class="pn-body" :class="{ 'has-agent': agentOpen, 'is-embedded': embedded }">
      <main class="pn-main">
        <LoadingIndicator v-if="loading" message="Loading notebook..." />
        <template v-else>
          <div v-if="!cells.length" class="pn-empty">
            <div class="text-gray-900 font-medium text-[13px]">An empty notebook.</div>
            <div class="text-gray-600 mt-1">Add a SQL cell to query the warehouse, a Python cell to analyse a table, a note, or a DataAttribute, metric, workflow, schema or HTML page to author one here. Drop a CSV or workbook anywhere on this page to add it as a saved frame.</div>
          </div>

          <div v-for="cell in cells" :id="`cell-${cell.id}`" :key="cell.id" class="pn-cell" :class="[{ 'is-flash': flash === cell.id, 'is-report': meta(cell).report }, `is-state-${cellState(cell)}`]">
            <div class="pn-cell-head">
              <span class="pn-state" :class="`is-${cellState(cell)}`" :title="stateTitle(cell)"><span v-if="cellState(cell) === 'running'" class="pn-spin" /></span>
              <span class="pn-engine" :class="`is-${engine(cell)}`">{{ engineLabel(cell) }}</span>
              <input
                :value="meta(cell).label || ''"
                type="text"
                class="pn-label"
                :placeholder="engine(cell) === 'markdown' ? 'Heading' : 'Name this cell'"
                aria-label="Cell name"
                :readonly="!canEdit"
                @change="patch(cell, { label: $event.target.value })"
              >
              <span class="pn-cell-id">{{ cell.id }}</span>
              <span v-if="meta(cell).author === 'agent'" class="pn-muted">written by the agent</span>
              <span v-if="canEdit" class="ml-auto flex items-center gap-3">
                <label class="pn-muted inline-flex items-center gap-1 cursor-pointer" title="Show this cell in the report">
                  <input type="checkbox" :checked="!!meta(cell).report" @change="patch(cell, { report: $event.target.checked })"> In report
                </label>
                <button v-if="busy[cell.id] && isKernel(cell)" type="button" class="btn-secondary-sm pn-action" @click="interruptCell(cell)">Stop</button>
                <button v-if="isSavable(cell)" type="button" class="btn-secondary-sm pn-action" :disabled="saving[cell.id]" :title="saveTitle(cell)" @click="saveCell(cell)">{{ saving[cell.id] ? 'Saving…' : (meta(cell).saved ? 'Save again' : 'Save') }}</button>
                <button v-if="isRunnable(cell)" type="button" class="btn-primary-sm pn-action" :disabled="busy[cell.id]" @click="runCell(cell)">{{ busy[cell.id] ? 'Running…' : 'Run' }}</button>
                <button type="button" class="pn-x" title="Remove this cell" @click="removeCell(cell)">×</button>
              </span>
            </div>

            <div v-if="hasFields(cell)" class="pn-fields">
              <label class="pn-field pn-field-wide">
                <span>{{ engine(cell) === 'html' ? 'Page title' : 'Name' }}</span>
                <input :value="meta(cell).name || ''" type="text" class="pn-field-input" :readonly="!canEdit" :placeholder="namePlaceholder(cell)" @change="patch(cell, { name: $event.target.value })">
              </label>
              <label v-if="engine(cell) === 'data_attribute'" class="pn-field">
                <span>Role</span>
                <select :value="meta(cell).role || 'attribute'" class="pn-field-input" :disabled="!canEdit" @change="patch(cell, { role: $event.target.value })">
                  <option value="attribute">attribute</option>
                  <option value="population">population</option>
                </select>
              </label>
              <label v-if="engine(cell) === 'data_attribute'" class="pn-field pn-field-grow">
                <span>Description</span>
                <input :value="meta(cell).description || ''" type="text" class="pn-field-input" :readonly="!canEdit" placeholder="What one row means and where it comes from" @change="patch(cell, { description: $event.target.value })">
              </label>
              <details v-if="engine(cell) === 'html'" class="pn-field pn-field-wide">
                <summary class="pn-muted cursor-pointer">Data sources ({{ sourcesCount(cell) }})</summary>
                <textarea :value="sourcesText(cell)" class="pn-sources" spellcheck="false" :readonly="!canEdit" placeholder='[{"kind": "metric", "id": 12}]' @change="patchSources(cell, $event.target.value)" />
              </details>
              <span v-if="meta(cell).saved" class="pn-saved" :class="{ 'is-stale': stale[cell.id] }" :title="meta(cell).saved.at ? `Saved ${formatWhen(meta(cell).saved.at)}` : ''">
                <router-link v-if="savedLink(cell)" :to="savedLink(cell)" class="pn-saved-link">{{ savedLabel(cell) }} #{{ meta(cell).saved.id }}</router-link>
                <template v-else>{{ savedLabel(cell) }} #{{ meta(cell).saved.id }}</template>
                <span v-if="meta(cell).saved.name" class="pn-muted">{{ ` · ${meta(cell).saved.name}` }}</span>
                <span v-if="stale[cell.id]" class="pn-changed ml-1" title="The source has changed since it was saved">changed since save</span>
              </span>
            </div>

            <div v-if="engine(cell) === 'markdown'" class="pn-note">
              <textarea
                v-if="editing[cell.id]"
                v-model="drafts[cell.id]"
                class="pn-note-editor"
                :rows="rowsFor(drafts[cell.id])"
                placeholder="Write in Markdown. Click away to save."
                @blur="saveSource(cell)"
                @keydown.meta.enter.prevent="saveSource(cell)"
                @keydown.ctrl.enter.prevent="saveSource(cell)"
              />
              <div v-else class="pn-prose" @click="canEdit && startEdit(cell)" v-html="markdown(source(cell) || (canEdit ? '*Click to write a note.*' : ''))" />
            </div>

            <template v-else>
              <div class="pn-code">
                <textarea
                  v-model="drafts[cell.id]"
                  class="ae-cell-editor"
                  spellcheck="false"
                  :rows="rowsFor(drafts[cell.id])"
                  :placeholder="placeholderFor(cell)"
                  :readonly="!canEdit"
                  @focus="editing[cell.id] = true"
                  @blur="saveSource(cell)"
                  @keydown.meta.enter.prevent="runCell(cell)"
                  @keydown.ctrl.enter.prevent="runCell(cell)"
                />
                <div class="pn-code-foot">
                  <span class="pn-muted"><template v-if="cell.execution_count">[{{ cell.execution_count }}] · </template><template v-if="isDirty(cell)">unsaved · </template>{{ isRunnable(cell) ? '⌘⏎ runs' : 'click away to save' }}</span>
                  <label v-if="engine(cell) === 'snowflake' || engine(cell) === 'postgres'" class="ml-auto inline-flex items-center gap-2 pn-muted">
                    Keep result as saved frame
                    <input :value="meta(cell).keep_as || ''" type="text" class="pn-keep" placeholder="frame-name" aria-label="Saved frame name" :readonly="!canEdit" @change="patch(cell, { keep_as: $event.target.value })">
                  </label>
                  <span v-else-if="engine(cell) === 'python'" class="ml-auto pn-muted"><code>kairon.saved("name")</code> · <code>kairon.query(sql)</code> · <code>kairon.save("name", df)</code></span>
                  <span v-else class="ml-auto pn-muted">{{ footHint(cell) }}</span>
                </div>
              </div>

              <div v-if="engine(cell) === 'html'" class="pn-result">
                <div class="pn-result-head"><span class="is-muted">Preview</span><span class="pn-muted">renders the source above in a sandbox; D3 is available</span></div>
                <HtmlFrame :html="drafts[cell.id] || source(cell)" />
              </div>

              <div v-else-if="meta(cell).last_run || (cell.outputs || []).length" class="pn-result">
                <div class="pn-result-head">
                  <span :class="runTone(cell)">{{ runText(cell) }}</span>
                  <span v-if="meta(cell).last_run && meta(cell).last_run.changed" class="pn-changed" title="This result differs from the previous run of this cell">changed</span>
                  <span v-if="meta(cell).last_run && meta(cell).last_run.at" class="pn-muted">{{ formatWhen(meta(cell).last_run.at) }}</span>
                  <span v-if="meta(cell).runs > 1" class="pn-muted">· {{ meta(cell).runs }} runs</span>
                  <span class="ml-auto flex items-center gap-3">
                    <router-link v-if="meta(cell).frame" :to="`/impact/frames/${meta(cell).frame.id}`" class="text-neutral-800">{{ meta(cell).frame.name }}</router-link>
                    <button v-if="meta(cell).frame" type="button" class="btn-secondary-sm" @click="downloadFrameId(meta(cell).frame.id, meta(cell).frame.name)">Download CSV</button>
                  </span>
                </div>
                <KernelOutput :outputs="cell.outputs || []" />
              </div>
              <div v-if="cellErrors[cell.id]" class="pn-error">{{ cellErrors[cell.id] }}</div>
            </template>

            <div v-if="canEdit" class="pn-insert">
              <button v-for="e in insertable" :key="e" type="button" @click="addCell(e, cell.id)">+ {{ ENGINE_LABELS[e] }}</button>
            </div>
          </div>

          <div v-if="!cells.length && canEdit" class="pn-insert pn-insert-first">
            <button v-for="e in insertable" :key="e" type="button" @click="addCell(e)">+ {{ ENGINE_LABELS[e] }}</button>
          </div>
        </template>
      </main>

      <aside v-if="!embedded" class="pn-rail">
        <div class="pn-rail-head">Saved frames <span class="pn-muted">{{ frames.length }}</span></div>
        <div v-if="!frames.length" class="pn-muted px-3 py-2">Keep a cell's result as a saved frame, call kairon.save, or drop a file here.</div>
        <div v-for="t in frames" :key="t.id" class="pn-tableRow">
          <router-link :to="`/impact/frames/${t.id}`" class="pn-tableName" :title="t.name">{{ leaf(t.name) }}</router-link>
          <span class="pn-muted">{{ (t.rows || 0).toLocaleString() }} rows · {{ t.kind }}<template v-if="t.versions > 1"> · {{ t.versions }} versions</template></span>
          <span class="flex items-center gap-2">
            <button type="button" class="pn-muted hover:text-gray-900" title="Download CSV" @click="downloadFrameId(t.id, t.name)">CSV</button>
            <template v-if="canEdit">
              <template v-if="deletingFrame === t.id">
                <button type="button" class="pn-muted text-red-dark hover:text-red-dark" title="Delete this frame and its file" @click="removeFrame(t)">delete</button>
                <button type="button" class="pn-muted hover:text-gray-900" @click="deletingFrame = null">keep</button>
              </template>
              <button v-else type="button" class="pn-muted hover:text-gray-900" title="Delete this saved frame" @click="deletingFrame = t.id">×</button>
            </template>
          </span>
        </div>
        <div class="pn-rail-head">Published <span class="pn-muted">{{ published.length }}</span></div>
        <div v-if="!published.length" class="pn-muted px-3 py-2">Publish freezes the notebook and its data record as a numbered version.</div>
        <div v-for="v in published" :key="v.version_id" class="pn-tableRow">
          <router-link :to="{ path: reportPath, query: { version: v.version_id } }" class="pn-tableName" :title="v.note || ''">{{ formatWhen(v.at) }}</router-link>
          <span class="pn-muted"><span>{{ v.by_name }}</span><template v-if="!v.ok"> · data changed</template></span>
        </div>
        <div v-if="notebook && notebook.kernels" class="pn-rail-head">Kernels <span class="pn-muted">{{ kernelsLive.length ? 'python, in this session' : 'starts with the first Python run' }}</span></div>
      </aside>

      <aside v-if="agentOpen && !embedded" class="pn-agent">
        <AgentConversation
          ref="conversation"
          :entry="entry"
          :context="{ notebookId }"
          :session-id="agentSessionId"
          :storage-key="`agent-notebook:${notebookId}`"
          compact
          auto-start
          @close="agentOpen = false"
          @open-full="openFull"
        />
      </aside>
    </div>

    <div v-if="dragging" class="pn-drop">Drop to add this file as a saved frame</div>
  </div>
</template>

<script>
import axios from 'axios';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import AgentConversation from '@/components/Beta/AgentConversation.vue';
import KernelOutput from './KernelOutput.vue';
import HtmlFrame from './HtmlFrame.vue';
import '@/components/Beta/agent.css';
import { sessionEntry } from '@/services/agentSessionController';
import { downloadFrame, waitForJob } from '@/utils/frameDownload';
import { uploadToProject } from '@/utils/projectUpload';
import { backTo } from '@/utils/backTo';
import { NOTEBOOK_CRUMBS, NOTEBOOKS_PATH, apiPath, notebookPath } from './impactNav';
import { dataAttributeLocation, dataTabLocation } from '../../../router/data_routes';
import { metricStudioLocation } from '../../../router/tenantadmin/metric_studio_routes';

const ENGINE_LABELS = {
  snowflake: 'SQL', postgres: 'Postgres', python: 'Python', markdown: 'Note',
  data_attribute: 'DataAttribute', metric: 'Metric', workflow: 'Workflow', schema: 'Schema', html: 'HTML',
};
const INSERTABLE = ['snowflake', 'postgres', 'python', 'markdown'];
const RUNNABLE = ['snowflake', 'postgres', 'python', 'data_attribute', 'metric', 'workflow', 'schema'];
const SAVABLE = ['data_attribute', 'metric', 'workflow', 'schema', 'html'];
const WITH_FIELDS = ['data_attribute', 'metric', 'workflow', 'schema', 'html'];
const SAVED_LABELS = { data_attribute: 'DataAttribute', metric: 'Metric', workflow: 'Workflow', schema: 'Schema', page: 'Page', html: 'Page' };
const PLACEHOLDERS = {
  snowflake: '-- A Snowflake query. {tenant_id} is substituted. Name a saved frame below to keep the full result.',
  postgres: '-- A read-only query against the app database.',
  python: '# Python. kairon.saved("frame-name") reads a saved frame, kairon.query(sql) the warehouse.\n# kairon.save("name", df) keeps one. A DataFrame as the last line shows as a table.',
  data_attribute: '-- The DataAttribute\'s SQL: one row per unit, {tenant_id} substituted. Run previews the rows; Save creates or updates the DataAttribute named above.',
  metric: '{\n  "steps": [{"workflow_id": 0, "role": "numerator"}],\n  "aggregation_type": "count",\n  "primary_key_columns": ["patient_id"]\n}\n// The metric config as JSON. Run validates it and previews the rows; Save creates or updates the metric named above.',
  workflow: '{\n  "steps": [{"workflow_id": 0}],\n  "primary_key_columns": ["patient_id"],\n  "output_columns": []\n}\n// The workflow\'s studio config as JSON. Run previews its output; Save creates or updates the workflow named above.',
  schema: '{\n  "table_name": "",\n  "columns": [{"name": "", "snowflake_type": "VARCHAR"}],\n  "primary_key_columns": []\n}\n// A parser schema as JSON. Run validates it; Save creates it.',
  html: '<div id="chart"></div>\n<script>\n  // HTML with D3 available as `d3`. The preview below renders this source in a sandbox. Save publishes it as a page.\n<\\/script>',
};
const FOOT_HINTS = {
  data_attribute: 'Run previews · Save writes the DataAttribute',
  metric: 'JSON config · Run validates and previews · Save writes the metric',
  workflow: 'JSON studio config · Run previews · Save writes the workflow',
  schema: 'JSON · Run validates · Save creates the schema',
  html: 'HTML with D3 · the preview below is live · Save publishes a page',
};
const REFRESH_MS = 6000;

export default {
  name: 'NotebookPage',
  components: { PageHeader, LoadingIndicator, AgentConversation, KernelOutput, HtmlFrame },
  props: {
    notebookId: { type: String, required: true },
    embedded: { type: Boolean, default: false },
  },
  data() {
    const scope = `${this.$store.state.tenant.current_tenant_slug || ''}:${this.$store.state.auth.current_user?.id || ''}`;
    return {
      entry: sessionEntry(`${scope}:notebook:${this.notebookId}`),
      notebook: null,
      loading: true,
      error: '',
      notice: '',
      drafts: {},
      draftBase: {},
      editing: {},
      busy: {},
      cellErrors: {},
      rerunning: false,
      rerunPhase: '',
      uploading: false,
      uploadPhase: '',
      publishing: false,
      published: [],
      runningCell: '',
      runQueue: [],
      deletingFrame: null,
      saving: {},
      stale: {},
      agentSessionId: String(this.$route.query.agent || '') || null,
      INSERTABLE,
      ENGINE_LABELS,
      dragging: false,
      agentOpen: !this.embedded && !!this.$route.query.agent,
      renaming: false,
      renameDraft: '',
      flash: '',
      refreshTimer: null,
      NOTEBOOK_CRUMBS,
      NOTEBOOKS_PATH,
    };
  },
  computed: {
    insertable() {
      return INSERTABLE;
    },
    canEdit() {
      return !!(this.notebook && this.notebook.is_mine) || !!this.$store.state.auth.current_user?.is_superuser;
    },
    title() {
      return this.notebook ? (this.notebook.title || 'Untitled notebook') : ' ';
    },
    subtitle() {
      if (!this.notebook) return '';
      return [this.notebook.owner_name, this.notebook.shared ? 'shared' : null, this.canEdit ? null : 'read only', `${this.cells.length} cells`].filter(Boolean).join(' · ');
    },
    cells() {
      return (this.notebook && this.notebook.notebook && this.notebook.notebook.cells) || [];
    },
    runnable() {
      return this.cells.filter((c) => RUNNABLE.includes(this.engine(c)));
    },
    frames() {
      const all = (this.notebook && this.notebook.frames) || [];
      const seen = new Map();
      all.forEach((f) => {
        const prev = seen.get(f.name);
        if (!prev) seen.set(f.name, { ...f, versions: 1 });
        else prev.versions += 1;
      });
      return [...seen.values()];
    },
    kernelsLive() {
      return (this.notebook && this.notebook.kernels && this.notebook.kernels.live) || [];
    },
    reportCount() {
      return this.cells.filter((c) => this.meta(c).report).length;
    },
    reportPath() {
      return `${notebookPath(this.notebookId)}/report`;
    },
    downloadHref() {
      return `/${apiPath(this.notebookId)}/download/`;
    },
  },
  watch: {
    cells(list) {
      list.forEach((c) => this.syncDraft(c));
    },
    agentOpen(open) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
      if (open) this.refreshTimer = setInterval(() => this.refreshQuietly(), REFRESH_MS);
    },
  },
  async mounted() {
    await this.load();
    if (this.embedded) this.refreshTimer = setInterval(() => this.refreshQuietly(), REFRESH_MS);
    const hash = String(window.location.hash || '').replace(/^#cell-/, '');
    if (hash) this.scrollToCell(hash);
  },
  beforeUnmount() {
    clearInterval(this.refreshTimer);
  },
  methods: {
    notebookPath,
    backTo,
    meta(cell) {
      return (cell.metadata && cell.metadata.kairon) || {};
    },
    engine(cell) {
      return cell.cell_type === 'markdown' ? 'markdown' : (this.meta(cell).engine || 'python');
    },
    isKernel(cell) {
      return ['python', 'snowflake', 'postgres'].includes(this.engine(cell));
    },
    isRunnable(cell) {
      return RUNNABLE.includes(this.engine(cell));
    },
    isSavable(cell) {
      return SAVABLE.includes(this.engine(cell));
    },
    hasFields(cell) {
      return WITH_FIELDS.includes(this.engine(cell));
    },
    namePlaceholder(cell) {
      return { data_attribute: 'DataAttribute name', metric: 'Metric name', workflow: 'Workflow name', schema: 'Schema name', html: 'Page title' }[this.engine(cell)] || 'Name';
    },
    footHint(cell) {
      return FOOT_HINTS[this.engine(cell)] || '';
    },
    savedLabel(cell) {
      const saved = this.meta(cell).saved || {};
      return SAVED_LABELS[saved.kind] || SAVED_LABELS[this.engine(cell)] || 'Saved';
    },
    savedLink(cell) {
      const saved = this.meta(cell).saved || {};
      if (!saved.id) return null;
      const kind = saved.kind || this.engine(cell);
      if (kind === 'data_attribute') return dataAttributeLocation(saved.id);
      if (kind === 'metric') return metricStudioLocation({ artifact: 'metric', id: saved.id });
      if (kind === 'page' || kind === 'html') return `/agent/page/${saved.id}`;
      if (kind === 'workflow') return dataTabLocation('Workflows');
      return null;
    },
    saveTitle(cell) {
      return { data_attribute: 'Create or update this DataAttribute on the platform', metric: 'Create or update this metric', workflow: 'Create or update this workflow', schema: 'Create this parser schema', html: 'Publish this as a page' }[this.engine(cell)] || 'Save';
    },
    sourcesCount(cell) {
      const v = this.meta(cell).sources;
      return Array.isArray(v) ? v.length : 0;
    },
    sourcesText(cell) {
      const v = this.meta(cell).sources;
      return v == null ? '' : JSON.stringify(v, null, 2);
    },
    async patchSources(cell, text) {
      let sources = [];
      if (text.trim()) {
        try {
          sources = JSON.parse(text);
        } catch {
          this.cellErrors[cell.id] = 'Data sources must be JSON.';
          return;
        }
      }
      await this.patch(cell, { sources });
    },
    async sha256(text) {
      const bytes = new TextEncoder().encode(String(text || ''));
      const digest = await crypto.subtle.digest('SHA-256', bytes);
      return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
    },
    async checkStale(cell) {
      const saved = this.meta(cell).saved;
      if (!saved || !saved.source_sha256) {
        this.stale[cell.id] = false;
        return;
      }
      const current = await this.sha256(this.drafts[cell.id] ?? this.source(cell));
      this.stale[cell.id] = current !== saved.source_sha256;
    },
    async saveCell(cell) {
      if (this.saving[cell.id] || !this.canEdit) return;
      this.saving[cell.id] = true;
      this.cellErrors[cell.id] = '';
      try {
        await this.saveSource(cell);
        const { data } = await axios.post(`${apiPath(this.notebookId)}/cells/${cell.id}/save/`, {});
        if (data.cell) this.replaceCell(data.cell);
        const saved = data.saved || {};
        this.notice = `Saved as ${SAVED_LABELS[saved.kind] || 'object'} #${saved.id}${saved.name ? ` (${saved.name})` : ''}.`;
        await this.checkStale(data.cell || cell);
      } catch (err) {
        this.cellErrors[cell.id] = err?.response?.data?.error || 'The cell could not be saved to the platform.';
      } finally {
        this.saving[cell.id] = false;
      }
    },
    source(cell) {
      return Array.isArray(cell.source) ? cell.source.join('') : (cell.source || '');
    },
    markdown(text) {
      return DOMPurify.sanitize(marked.parse(String(text || ''), { breaks: true, gfm: true }));
    },
    engineLabel(cell) {
      return ENGINE_LABELS[this.engine(cell)] || 'Cell';
    },
    placeholderFor(cell) {
      return PLACEHOLDERS[this.engine(cell)] || '';
    },
    leaf(name) {
      return String(name || '').split('/').pop();
    },
    rowsFor(text) {
      return Math.min(Math.max(String(text || '').split('\n').length + 1, 3), 30);
    },
    formatWhen(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    async load() {
      this.error = '';
      try {
        const { data } = await axios.get(`${apiPath(this.notebookId)}/`);
        this.applyNotebook(data);
        this.loadPublished();
      } catch (err) {
        this.error = err?.response?.status === 404 ? 'No notebook with that id on this tenant.' : 'The notebook could not be loaded.';
      } finally {
        this.loading = false;
      }
    },
    async refreshQuietly() {
      try {
        const { data } = await axios.get(`${apiPath(this.notebookId)}/`);
        this.applyNotebook(data);
      } catch {
        // the next refresh will catch up
      }
    },
    applyNotebook(data) {
      this.notebook = data;
      this.cells.forEach((c) => { this.syncDraft(c); if (this.meta(c).saved) this.checkStale(c); });
    },
    isDirty(cell) {
      return cell.id in this.drafts && this.drafts[cell.id] !== this.source(cell);
    },
    syncDraft(cell) {
      const base = this.source(cell);
      const fresh = !(cell.id in this.drafts);
      const moved = this.draftBase[cell.id] !== base;
      if (fresh || (moved && !this.editing[cell.id])) {
        this.drafts[cell.id] = base;
        this.draftBase[cell.id] = base;
      }
    },
    startEdit(cell) {
      this.syncDraft(cell);
      this.editing[cell.id] = true;
    },
    replaceCell(updated) {
      const list = this.cells;
      const i = list.findIndex((c) => c.id === updated.id);
      if (i >= 0) list.splice(i, 1, updated);
      this.draftBase[updated.id] = this.source(updated);
    },
    async patch(cell, payload) {
      if (!this.canEdit) return false;
      try {
        const { data } = await axios.patch(`${apiPath(this.notebookId)}/cells/${cell.id}/`, payload);
        this.replaceCell(data);
        this.cellErrors[cell.id] = '';
        return true;
      } catch (err) {
        this.cellErrors[cell.id] = err?.response?.data?.error || 'The cell could not be saved.';
        return false;
      }
    },
    async saveSource(cell) {
      this.editing[cell.id] = false;
      if (!this.isDirty(cell)) return true;
      const ok = await this.patch(cell, { source: this.drafts[cell.id] });
      if (this.meta(cell).saved) this.checkStale(cell);
      return ok;
    },
    async addCell(engine, after = null) {
      try {
        const { data } = await axios.post(`${apiPath(this.notebookId)}/cells/`, { engine, source: '', after });
        const list = this.cells;
        const i = after ? list.findIndex((c) => c.id === after) : -1;
        if (i >= 0) list.splice(i + 1, 0, data);
        else list.push(data);
        this.drafts[data.id] = '';
        this.draftBase[data.id] = '';
        this.editing[data.id] = engine === 'markdown';
        this.$nextTick(() => {
          const el = document.querySelector(`#cell-${CSS.escape(data.id)} textarea`);
          if (el) el.focus();
        });
      } catch (err) {
        this.error = err?.response?.data?.error || 'The cell could not be added.';
      }
    },
    async removeCell(cell) {
      try {
        await axios.delete(`${apiPath(this.notebookId)}/cells/${cell.id}/`);
        const list = this.cells;
        const i = list.findIndex((c) => c.id === cell.id);
        if (i >= 0) list.splice(i, 1);
      } catch (err) {
        this.cellErrors[cell.id] = err?.response?.data?.error || 'The cell could not be removed.';
      }
    },
    async runCell(cell) {
      if (this.busy[cell.id] || !this.canEdit) return;
      this.busy[cell.id] = true;
      this.cellErrors[cell.id] = '';
      this.editing[cell.id] = false;
      try {
        const body = this.isDirty(cell) ? { source: this.drafts[cell.id] } : {};
        if (body.source !== undefined) this.draftBase[cell.id] = body.source;
        const { data } = await axios.post(`${apiPath(this.notebookId)}/cells/${cell.id}/run/`, body);
        if (data.session_id && !this.entry.sessionId) this.entry.sessionId = data.session_id;
        const job = await waitForJob(data.job_id, null, { every: 1000, limit: 7200 });
        if (['pending', 'in_progress'].includes(job.status)) this.cellErrors[cell.id] = 'The cell is still running. Refresh the page later to see its result.';
        else if (job.status !== 'completed') this.cellErrors[cell.id] = job.error_message || 'The cell did not finish.';
        await this.refreshQuietly();
      } catch (err) {
        this.cellErrors[cell.id] = err?.response?.data?.error || 'The cell could not be run.';
      } finally {
        this.busy[cell.id] = false;
      }
    },
    async interruptCell(cell) {
      try {
        await axios.post(`${apiPath(this.notebookId)}/cells/${cell.id}/interrupt/`);
      } catch (err) {
        this.cellErrors[cell.id] = err?.response?.data?.error || 'The kernel could not be interrupted.';
      }
    },
    async loadPublished() {
      try {
        const { data } = await axios.get(`${apiPath(this.notebookId)}/published/`);
        this.published = data;
      } catch {
        this.published = [];
      }
    },
    async publish() {
      this.publishing = true;
      try {
        const { data } = await axios.post(`${apiPath(this.notebookId)}/publish/`, {});
        const m = data.manifest || {};
        const reads = (m.inputs || []).length;
        const writes = (m.outputs || []).length;
        this.notice = m.ok
          ? `Published. ${reads} input${reads === 1 ? '' : 's'} and ${writes} saved frame${writes === 1 ? '' : 's'} recorded and verified.`
          : 'Published, but a frame this notebook read no longer matches what it read. Open the report for the data record.';
        await this.loadPublished();
        await this.refreshQuietly();
      } catch (err) {
        this.error = err?.response?.data?.error || 'The notebook could not be published.';
      } finally {
        this.publishing = false;
      }
    },
    async restartKernel() {
      try {
        await axios.post(`${apiPath(this.notebookId)}/kernel/restart/`, {});
        this.notice = 'Kernels shut down. The next run starts fresh.';
        await this.refreshQuietly();
      } catch (err) {
        this.error = err?.response?.data?.error || 'The kernel could not be restarted.';
      }
    },
    async rerunAll() {
      if (this.rerunning) return;
      this.rerunning = true;
      this.rerunPhase = 'Queued…';
      try {
        const { data } = await axios.post(`${apiPath(this.notebookId)}/rerun/`);
        let job = { status: 'pending' };
        this.runQueue = this.runnable.map((c) => c.id);
        for (let i = 0; i < 2000 && ['pending', 'in_progress'].includes(job.status); i += 1) {
          await new Promise((resolve) => { setTimeout(resolve, 2000); });
          job = (await axios.get(`api/jobs/${data.job_id}/status/`)).data;
          this.rerunPhase = job.progress_message || `${job.progress_current || 0} of ${job.progress_total || 0}`;
          let current = (job.result_data && job.result_data.current) || '';
          if (!current && /^Running /.test(job.progress_message || '')) {
            const label = job.progress_message.replace(/^Running /, '');
            const hit = this.cells.find((c) => (this.meta(c).label || c.id) === label);
            current = hit ? hit.id : '';
          }
          if (current !== this.runningCell) {
            this.runningCell = current;
            if (current) this.scrollToCell(current);
            await this.refreshQuietly();
          } else if (i % 3 === 2) await this.refreshQuietly();
        }
        this.runningCell = '';
        this.runQueue = [];
        await this.refreshQuietly();
        if (job.status === 'completed') {
          const changed = (job.result_data && job.result_data.changed) || [];
          this.notice = changed.length ? `Run complete. ${changed.length} cell${changed.length === 1 ? '' : 's'} changed: ${changed.join(', ')}.` : 'Run complete. Nothing moved.';
        } else {
          this.error = job.error_message || 'The run did not finish.';
        }
      } catch (err) {
        this.error = err?.response?.data?.error || 'The run could not be started.';
      } finally {
        this.rerunning = false;
        this.rerunPhase = '';
      }
    },
    cellState(cell) {
      if (this.busy[cell.id] || this.runningCell === cell.id) return 'running';
      if (this.runQueue.includes(cell.id)) {
        const done = this.runQueue.indexOf(this.runningCell);
        if (done === -1 || this.runQueue.indexOf(cell.id) > done) return 'queued';
      }
      const run = this.meta(cell).last_run;
      if (!run || this.engine(cell) === 'markdown') return 'idle';
      return run.status === 'failed' ? 'failed' : 'done';
    },
    stateTitle(cell) {
      const state = this.cellState(cell);
      const run = this.meta(cell).last_run || {};
      return { running: 'Running now', queued: 'Waiting its turn in Run all', failed: `Failed: ${run.error || ''}`, done: `Ran ${run.at ? new Date(run.at).toLocaleTimeString() : ''}`, idle: 'Not run yet' }[state];
    },
    runTone(cell) {
      const run = this.meta(cell).last_run;
      if (!run) return 'is-muted';
      return run.status === 'failed' ? 'is-error' : 'is-ok';
    },
    runText(cell) {
      const run = this.meta(cell).last_run;
      if (!run) return 'Output';
      if (run.status === 'failed') return 'Failed';
      const secs = typeof run.elapsed_ms === 'number' ? ` · ${(run.elapsed_ms / 1000).toFixed(1)}s` : '';
      if (run.row_count != null) return `${Number(run.row_count).toLocaleString()} rows${secs}`;
      return `${(cell.outputs || []).length} outputs${secs}`;
    },
    async removeFrame(t) {
      try {
        await axios.delete(`api/impact_analytics/frames/${t.id}/`);
        this.deletingFrame = null;
        await this.refreshQuietly();
      } catch (err) {
        this.error = err?.response?.data?.error || 'The frame could not be deleted.';
      }
    },
    async downloadFrameId(id, name) {
      const err = await downloadFrame({ id, name });
      if (err) this.error = err;
    },
    async downloadNotebook() {
      try {
        const { data } = await axios.get(`${apiPath(this.notebookId)}/download/`, { responseType: 'blob' });
        const url = URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${String(this.title).replace(/[^a-z0-9]+/gi, '_')}.ipynb`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        this.error = err?.response?.status === 403 ? 'Downloading the notebook needs the export permission.' : 'The notebook could not be downloaded.';
      }
    },
    scrollToCell(id) {
      this.flash = id;
      setTimeout(() => { if (this.flash === id) this.flash = ''; }, 1600);
      this.$nextTick(() => {
        const el = document.getElementById(`cell-${id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    openFull(sessionId) {
      const { href } = this.$router.resolve({ path: notebookPath(this.notebookId), query: { agent: sessionId } });
      window.open(href, '_blank', 'noopener');
    },
    startRename() {
      this.renameDraft = this.title;
      this.renaming = true;
      this.$nextTick(() => this.$refs.renameInput && this.$refs.renameInput.focus());
    },
    async rename() {
      const title = this.renameDraft.trim();
      if (!title) return;
      try {
        const { data } = await axios.patch(`${apiPath(this.notebookId)}/`, { title });
        this.notebook = { ...this.notebook, title: data.title };
        this.renaming = false;
      } catch (err) {
        this.error = err?.response?.data?.error || 'The notebook could not be renamed.';
      }
    },
    onDragOver(event) {
      if (!event.dataTransfer || !Array.from(event.dataTransfer.types || []).includes('Files')) return;
      this.dragging = true;
    },
    onDragLeave(event) {
      if (event.relatedTarget && this.$el.contains(event.relatedTarget)) return;
      this.dragging = false;
    },
    onDrop(event) {
      this.dragging = false;
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
      if (file) this.upload(file);
    },
    onPick(event) {
      const file = event.target.files[0];
      event.target.value = '';
      if (file) this.upload(file);
    },
    async upload(file) {
      if (this.uploading || !this.canEdit) return;
      this.uploading = true;
      this.error = '';
      try {
        if (/\.ipynb$/i.test(file.name)) {
          this.uploadPhase = 'Uploading notebook…';
          const { data: presigned } = await axios.post('/api/etl_module/upload/', { filename: file.name, content_type: 'application/x-ipynb+json' });
          const put = await fetch(presigned.upload_url, { method: 'PUT', headers: { 'Content-Type': 'application/x-ipynb+json' }, body: file });
          if (!put.ok) throw new Error(`Upload failed (${put.status})`);
          const { data } = await axios.post(`${apiPath(this.notebookId)}/upload/`, { s3_key: presigned.s3_key });
          this.notice = `Replaced the notebook with ${file.name} (${data.cells} cells).`;
        } else {
          const result = await uploadToProject(file, this.notebookId, (phase) => { this.uploadPhase = phase; });
          if (result.note) this.notice = result.note;
          else if (result.frames.length) this.notice = `Added ${result.frames.map((f) => this.leaf(f.name)).join(', ')} as ${result.frames.length === 1 ? 'a saved frame' : 'saved frames'}.`;
        }
        await this.refreshQuietly();
      } catch (err) {
        this.error = err?.response?.data?.error || (err && err.message) || 'Upload failed.';
      } finally {
        this.uploading = false;
        this.uploadPhase = '';
      }
    },
  },
};
</script>

<style scoped>
.pn { position: relative; min-height: 100vh; }
.pn.is-embedded { min-height: 0; flex: 1; overflow-y: auto; display: flex; flex-direction: column; }
.pn.is-embedded .pn-toolbar { position: sticky; top: 0; z-index: 2; }
.pn-body.is-embedded { grid-template-columns: minmax(0, 1fr); padding: 8px 12px 64px; }
.pn-toolbar { display: flex; align-items: center; gap: 10px; padding: 8px 12px; border-bottom: 1px solid #e4e0d9; background: #fff; font-size: 12px; }
.pn-toolbar-title { font-weight: 600; color: #1a1918; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pn-toolbar-actions { margin-left: auto; display: flex; gap: 6px; }
.pn-action { font-size: 11px; font-weight: 500; padding: 4px 8px; line-height: 1.2; }
.pn-body { display: grid; grid-template-columns: minmax(0, 1fr) 260px; gap: 16px; padding: 16px 24px 128px; align-items: start; }
.pn-body.has-agent { grid-template-columns: minmax(0, 1fr) 260px 440px; }
.pn-main { min-width: 0; display: flex; flex-direction: column; gap: 10px; }
.pn-empty { background: #fff; border: 1px dashed #d6d2ca; border-radius: 8px; padding: 24px; font-size: 12px; color: #56534d; }
.pn-cell { background: #fff; border: 1px solid #e4e0d9; border-left: 4px solid #e4e0d9; border-radius: 8px; overflow: hidden; transition: box-shadow 0.3s, border-color 0.3s; }
.pn-cell.is-state-running { border-left-color: #2f6b9a; box-shadow: 0 0 0 2px rgba(47, 107, 154, 0.15); }
.pn-cell.is-state-done { border-left-color: #2f6b49; }
.pn-cell.is-state-failed { border-left-color: #c23a28; }
.pn-cell.is-state-queued { border-left-color: #d79b2b; }
.pn-state { width: 10px; height: 10px; border-radius: 50%; background: #d6d2ca; flex: none; display: inline-flex; align-items: center; justify-content: center; }
.pn-state.is-done { background: #2f6b49; }
.pn-state.is-failed { background: #c23a28; }
.pn-state.is-queued { background: #d79b2b; }
.pn-state.is-running { background: transparent; }
.pn-spin { width: 10px; height: 10px; border-radius: 50%; border: 2px solid #c9dcea; border-top-color: #2f6b9a; animation: pn-spin 0.8s linear infinite; }
@keyframes pn-spin { to { transform: rotate(360deg); } }
.pn-cell.is-report .pn-cell-head { background: #fff6f2; }
.pn-cell.is-flash { box-shadow: 0 0 0 3px rgba(247, 103, 52, 0.35); }
.pn-cell-head { display: flex; align-items: center; gap: 10px; padding: 8px 12px; border-bottom: 1px solid #efece6; font-size: 11px; color: #56534d; }
.pn-engine { font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; font-size: 10px; padding: 2px 6px; border-radius: 4px; background: #f0eee8; color: #56534d; }
.pn-engine.is-r { background: #ece6f5; color: #5a3f85; }
.pn-engine.is-python { background: #e3eef6; color: #2a4d65; }
.pn-engine.is-markdown { background: #f6f5f3; }
.pn-engine.is-data_attribute, .pn-engine.is-metric, .pn-engine.is-workflow, .pn-engine.is-schema { background: #fdebdf; color: #8a3b14; }
.pn-engine.is-html { background: #e6f1ea; color: #1f5a3a; }
.pn-fields { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 8px 14px; padding: 6px 12px 8px; border-bottom: 1px solid #efece6; background: #fbfaf8; font-size: 11px; }
.pn-field { display: flex; flex-direction: column; gap: 2px; color: #8b877f; min-width: 140px; }
.pn-field-wide { flex: 1 1 260px; }
.pn-field-grow { flex: 2 1 320px; }
.pn-field-input { border: 1px solid #ded9d0; border-radius: 4px; padding: 3px 6px; font-size: 12px; color: #1a1918; background: #fff; }
.pn-field-input:focus { outline: none; border-color: #b0aca3; }
.pn-sources { display: block; width: 100%; min-height: 72px; margin-top: 4px; border: 1px solid #ded9d0; border-radius: 4px; padding: 4px 6px; font-family: 'IBM Plex Mono', monospace; font-size: 11px; }
.pn-saved { display: inline-flex; align-items: center; gap: 4px; align-self: center; font-size: 11px; color: #1f6a45; background: #e7f3ec; border-radius: 4px; padding: 2px 8px; }
.pn-saved.is-stale { color: #7d5a00; background: #fff1d6; }
.pn-saved-link { font-weight: 600; }
.pn-saved-link:hover { text-decoration: underline; }
.pn-label { flex: 0 1 320px; min-width: 120px; border: 0; border-bottom: 1px dashed transparent; background: transparent; font-size: 12px; font-weight: 600; color: #1a1918; padding: 2px 0; }
.pn-label:focus { outline: none; border-bottom-color: #cfcabf; }
.pn-cell-id { font-family: 'IBM Plex Mono', monospace; color: #b0aca3; }
.pn-muted { color: #8b877f; font-size: 11px; }
.pn-x { color: #b0aca3; font-size: 16px; line-height: 1; padding: 0 4px; }
.pn-x:hover { color: #c23a28; }
.pn-code { background: #1a1918; }
.pn-code-foot { display: flex; align-items: center; gap: 10px; padding: 4px 12px; background: #f6f5f3; border-top: 1px solid #e4e0d9; }
.pn-keep { width: 160px; border: 1px solid #ded9d0; border-radius: 4px; padding: 2px 6px; font-size: 11px; font-family: 'IBM Plex Mono', monospace; background: #fff; }
.pn-note-editor { display: block; width: 100%; box-sizing: border-box; border: 0; padding: 10px 12px; font-size: 13px; line-height: 1.5; font-family: inherit; resize: vertical; }
.pn-note-editor:focus { outline: none; background: #fbfaf8; }
.pn-prose { padding: 10px 14px; font-size: 13px; line-height: 1.55; color: #38352f; cursor: text; min-height: 36px; }
.pn-prose :deep(h1) { font-size: 18px; font-weight: 600; margin: 4px 0 8px; }
.pn-prose :deep(h2) { font-size: 15px; font-weight: 600; margin: 4px 0 6px; }
.pn-prose :deep(h3) { font-size: 13px; font-weight: 600; margin: 4px 0; }
.pn-prose :deep(p) { margin: 0 0 8px; }
.pn-prose :deep(ul), .pn-prose :deep(ol) { padding-left: 20px; margin: 0 0 8px; }
.pn-prose :deep(code) { font-family: 'IBM Plex Mono', monospace; font-size: 12px; background: #f0eee8; padding: 0 3px; border-radius: 3px; }
.pn-result { border-top: 1px solid #e4e0d9; }
.pn-result-head { display: flex; align-items: center; gap: 10px; padding: 6px 12px; font-size: 11px; background: #fbfaf8; border-bottom: 1px solid #efece6; }
.pn-result-head .is-ok { color: #1f6a45; font-weight: 600; }
.pn-result-head .is-error { color: #c23a28; font-weight: 600; }
.pn-changed { background: #fff1d6; color: #7d5a00; border-radius: 4px; padding: 1px 6px; font-weight: 600; }
.pn-error { padding: 8px 12px; color: #c23a28; font-size: 12px; white-space: pre-wrap; }
.pn-insert { display: flex; gap: 6px; padding: 4px 12px; background: #fbfaf8; border-top: 1px solid #efece6; }
.pn-insert button { font-size: 11px; color: #8b877f; padding: 2px 6px; border-radius: 4px; }
.pn-insert button:hover { color: #1a1918; background: #f0eee8; }
.pn-insert-first { background: transparent; border: 0; padding: 0; }
.pn-insert-first button { font-size: 12px; border: 1px solid #ded9d0; background: #fff; padding: 6px 12px; }
.pn-rail { position: sticky; top: 16px; background: #fff; border: 1px solid #e4e0d9; border-radius: 8px; font-size: 12px; max-height: calc(100vh - 120px); overflow: auto; }
.pn-rail-head { padding: 7px 12px; background: #f6f5f3; border-top: 1px solid #e4e0d9; border-bottom: 1px solid #e4e0d9; font-size: 10px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: #56534d; display: flex; gap: 6px; align-items: baseline; }
.pn-rail-head:first-child { border-top: 0; }
.pn-rail-head .pn-muted { text-transform: none; letter-spacing: 0; font-weight: 500; }
.pn-tableRow { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 2px 8px; padding: 6px 12px; border-bottom: 1px solid #f3f1ec; align-items: center; }
.pn-tableName { grid-column: 1 / -1; color: #1a1918; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pn-agent { position: sticky; top: 16px; height: calc(100vh - 120px); display: flex; flex-direction: column; min-height: 0; }
.pn-agent :deep(.ae-card) { height: 100%; }
.pn-drop { position: fixed; inset: 0; z-index: 50; display: flex; align-items: center; justify-content: center; background: rgba(247, 103, 52, 0.12); border: 3px dashed #f76734; font-size: 16px; font-weight: 600; color: #1a1918; pointer-events: none; }
</style>
